// SPDX-FileCopyrightText: Copyright (C) 2022-2024 Shanghai coScene Information Technology Co., Ltd.<hi@coscene.io>
// SPDX-License-Identifier: MPL-2.0

// This Source Code Form is subject to the terms of the Mozilla Public
// License, v2.0. If a copy of the MPL was not distributed with this
// file, You can obtain one at http://mozilla.org/MPL/2.0/

interface DomainConfig {
  env: string;
  logo: string;
  authStatusCookieName: string;
  authStatusCookieDomain: string;
  webDomain: string;
  ssoDomain: string;
}

const DEFAULT_DOMAN_CONFIG: { [domain: string]: DomainConfig } = {
  default: {
    env: "local",
    logo: "coscene",
    authStatusCookieName: "coSceneAuthStatusDev",
    authStatusCookieDomain: "localhost",
    webDomain: "dev.coscene.cn",
    ssoDomain: "sso.dev.coscene.cn",
  },
};
const DEFAULT_DEV_OBJECT_STORAGE_BASE_URL = "coscene-dev-honeybee-sh.tos-cn-shanghai.volces.com";
const DEFAULT_WEB_PUBLIC_PATH = "/viz/";

declare global {
  interface Window {
    cosConfig?: {
      CS_HONEYBEE_BASE_URL?: string;
      GITHUB_SHA?: string;
      IMAGE_TAG?: string;
      LAYOUT_TEMPLATE_INDEX_OSS_URL?: string;
      RELEASE_TAG?: string;
      LAST_BUILD_TIME?: string;
      NPM_PACKAGE_VERSION?: string;
      SENTRY_ENABLED?: boolean;
      SENTRY_HONEYBEE_DSN?: string;
      VITE_APP_BASE_API_PORT?: string;
      VITE_APP_BASE_API_URL?: string;
      VITE_APP_PROJECT_ENV?: string;
      VITE_APP_BFF_URL?: string;
      DEFAULT_TOPIC_PREFIX_OPEN?: { [domain: string]: string };
      DOC_BASE_URL?: string;
      DOMAIN_CONFIG?: { [domain: string]: DomainConfig };
      POSTHOG?: {
        api_host: string;
        token: string;
      };
      LANGUAGE?: {
        default: string;
        options: string[];
      };
      COSTUDIO_DOWNLOAD_URL?: string;
      OFFICIAL_WEB_URL?: string;
      COORDINATOR_URL?: string;
      S3_REGION?: string;
      OBJECT_STORAGE_BASE_URL?: string;
    };
    buildTime?: string;
    cosConfigRemoteHostname?: string;
  }
}

export function getAppConfig(): NonNullable<Window["cosConfig"]> {
  const cosConfig = typeof window !== "undefined" ? (window.cosConfig ?? {}) : {};

  const appConfig = {
    VITE_APP_BASE_API_PORT:
      cosConfig.VITE_APP_BASE_API_PORT ?? process.env.VITE_APP_BASE_API_PORT ?? "443",
    VITE_APP_BASE_API_URL:
      cosConfig.VITE_APP_BASE_API_URL ??
      process.env.VITE_APP_BASE_API_URL ??
      "https://api.dev.coscene.cn",
    VITE_APP_PROJECT_ENV:
      cosConfig.VITE_APP_PROJECT_ENV ?? process.env.VITE_APP_PROJECT_ENV ?? "local",
    CS_HONEYBEE_BASE_URL:
      cosConfig.CS_HONEYBEE_BASE_URL ?? process.env.CS_HONEYBEE_BASE_URL ?? "http://localhost:8080",
    VITE_APP_BFF_URL:
      cosConfig.VITE_APP_BFF_URL ?? process.env.VITE_APP_BFF_URL ?? "https://bff.dev.coscene.cn",
    IMAGE_TAG: process.env.IMAGE_TAG,
    GITHUB_SHA: process.env.GITHUB_SHA,
    RELEASE_TAG:
      process.env.GITHUB_SHA && process.env.IMAGE_TAG === "latest"
        ? process.env.GITHUB_SHA
        : process.env.IMAGE_TAG,
    LAST_BUILD_TIME: process.env.LAST_BUILD_TIME,
    NPM_PACKAGE_VERSION: process.env.NPM_PACKAGE_VERSION,
    LAYOUT_TEMPLATE_INDEX_OSS_URL:
      cosConfig.LAYOUT_TEMPLATE_INDEX_OSS_URL ??
      process.env.LAYOUT_TEMPLATE_INDEX_OSS_URL ??
      "http://coscene-artifacts-production.oss-cn-hangzhou.aliyuncs.com/honeybee_layouts/dev/index.json",
    SENTRY_HONEYBEE_DSN: cosConfig.SENTRY_HONEYBEE_DSN ?? "",
    SENTRY_ENABLED: cosConfig.SENTRY_ENABLED ?? false,
    DEFAULT_TOPIC_PREFIX_OPEN: cosConfig.DEFAULT_TOPIC_PREFIX_OPEN ?? {},
    DOC_BASE_URL: cosConfig.DOC_BASE_URL ?? "https://dev.docs.coscene.cn",
    DOMAIN_CONFIG: cosConfig.DOMAIN_CONFIG ?? DEFAULT_DOMAN_CONFIG,
    POSTHOG: cosConfig.POSTHOG ?? {
      api_host: "",
      token: "",
    },
    LANGUAGE: cosConfig.LANGUAGE ?? {
      default: "en",
      options: ["en", "zh"],
    },
    COSTUDIO_DOWNLOAD_URL:
      cosConfig.COSTUDIO_DOWNLOAD_URL ??
      process.env.COSTUDIO_DOWNLOAD_URL ??
      "https://download.coscene.cn/coStudio/packages",

    OFFICIAL_WEB_URL: cosConfig.OFFICIAL_WEB_URL ?? "https://www.coscene.cn",

    COORDINATOR_URL: cosConfig.COORDINATOR_URL ?? "https://coordinator.dev.coscene.cn",

    S3_REGION: cosConfig.S3_REGION ?? "cn-shanghai",

    OBJECT_STORAGE_BASE_URL:
      cosConfig.OBJECT_STORAGE_BASE_URL ??
      (process.env.NODE_ENV === "development" ? DEFAULT_DEV_OBJECT_STORAGE_BASE_URL : undefined),
  };

  if (
    typeof window !== "undefined" &&
    (!window.cosConfig || Object.keys(window.cosConfig).length === 0)
  ) {
    window.cosConfig = appConfig;
  }

  return appConfig;
}

function normalizeWebBasePath(path: string | undefined): string {
  const trimmedPath = path?.trim();
  const normalizedPath =
    trimmedPath == undefined || trimmedPath.length === 0 ? DEFAULT_WEB_PUBLIC_PATH : trimmedPath;
  const withLeadingSlash = normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;
  return withLeadingSlash.endsWith("/") ? withLeadingSlash : `${withLeadingSlash}/`;
}

export function getWebBasePath(): string {
  const configuredPath =
    typeof HONEYBEE_WEB_PUBLIC_PATH === "string"
      ? HONEYBEE_WEB_PUBLIC_PATH
      : process.env.HONEYBEE_WEB_PUBLIC_PATH;

  return normalizeWebBasePath(configuredPath);
}

export function getWebBasePathname(): string {
  const basePath = getWebBasePath();
  return basePath === "/" ? "/" : basePath.replace(/\/$/, "");
}

export function getWebAssetPath(assetPath: string): string {
  const normalizedAssetPath = assetPath.replace(/^\//, "");
  return `${getWebBasePath()}${normalizedAssetPath}`;
}

export function getDomainConfig(): DomainConfig {
  const appConfig = getAppConfig();
  const hostname = typeof window !== "undefined" ? window.location.hostname : undefined;
  const remoteConfigHostname =
    typeof window !== "undefined" ? window.cosConfigRemoteHostname : undefined;

  return (
    (hostname ? appConfig.DOMAIN_CONFIG?.[hostname] : undefined) ??
    (remoteConfigHostname ? appConfig.DOMAIN_CONFIG?.[remoteConfigHostname] : undefined) ??
    appConfig.DOMAIN_CONFIG?.default ??
    DEFAULT_DOMAN_CONFIG.default!
  );
}

export function getAuthStatusCookieName(): string {
  return getDomainConfig().authStatusCookieName;
}
